// dual-select.component.ts
import {
  Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectableItem } from './dtos/selectable-item';
import { forkJoin, map, Observable, of } from 'rxjs';
import { NotificationService } from '../../services';

@Component({
  selector: 'canvas-dual-select',
  templateUrl: './dual-select.component.html',
  styleUrls: ['./dual-select.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DualSelectComponent),
      multi: true,
    },
  ],
})
export class DualSelectComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  @Input() canRemoveItem?: (
    item: SelectableItem
  ) => Observable<boolean> | boolean;
  @Input() items: SelectableItem[] = [];
  @Output() selectionChange = new EventEmitter<SelectableItem[]>();

  selectableItems: SelectableItem[] = [];
  internalSelectedItems: SelectableItem[] = [];
  initialSelectedItems?: (SelectableItem | number | string)[];
  pendingSelection: { [key: string]: boolean } = {};

  // For ControlValueAccessor
  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(private notificationService: NotificationService) {}

  ngOnInit(): void {
    this.prepareSelectedItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.prepareSelectedItems();
    }
  }

  writeValue(value: SelectableItem[] | number[] | string[]): void {
    this.initialSelectedItems = value;
    if (!value || !this.items) {
      this.internalSelectedItems = [];
      this.resetSelectableItems();
      return;
    }

    this.prepareSelectedItems();
  }

  private prepareSelectedItems() {
    if (this.initialSelectedItems) {
      const ids = this.initialSelectedItems.map(
        (v: SelectableItem | number | string) =>
          typeof v === 'object' ? v.id : v
      );

      this.internalSelectedItems = this.items.filter((item) =>
        ids.includes(item.id)
      );
    }

    this.resetSelectableItems();
    this.emitSelectionChange();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // implement if needed
  }

  resetSelectableItems() {
    const selectedIds = new Set(this.internalSelectedItems.map((i) => i.id));
    this.selectableItems = this.items.filter((i) => !selectedIds.has(i.id));
    this.pendingSelection = {};
  }

  togglePendingSelection(item: SelectableItem) {
    this.pendingSelection[item.id] = !this.pendingSelection[item.id];
  }

  addToSelectedItems(items?: SelectableItem | SelectableItem[]) {
    const toAdd = items
      ? Array.isArray(items)
        ? items
        : [items]
      : this.selectableItems.filter((i) => this.pendingSelection[i.id]);

    toAdd.forEach((item) => {
      if (!this.internalSelectedItems.find((i) => i.id === item.id)) {
        this.internalSelectedItems.push(item);
      }
      delete this.pendingSelection[item.id];
    });

    this.resetSelectableItems();
    this.emitSelectionChange();
  }

  removeFromSelectedItems(items?: SelectableItem | SelectableItem[]) {
    const toRemove = items
      ? Array.isArray(items)
        ? items
        : [items]
      : this.internalSelectedItems.filter((i) => this.pendingSelection[i.id]);

    if (this.canRemoveItem) {
      const checks = toRemove.map((item) => {
        const result = this.canRemoveItem!(item);
        return result instanceof Observable
          ? result.pipe(map((allowed) => ({ item, allowed })))
          : of({ item, allowed: result });
      });

      forkJoin(checks).subscribe((results) => {
        const allowedItems = results
          .filter((r) => r.allowed)
          .map((r) => r.item);
        const blockedItems = results
          .filter((r) => !r.allowed)
          .map((r) => r.item);

        if (blockedItems.length) {
          // You could use a toast or warning service here
          const names = blockedItems.map((i) => i.name).join(', ');
          this.notificationService.showError(
            `Cannot remove: ${names} due to existing dependencies.`
          );
        }

        this._removeItemsInternal(allowedItems);
      });
    } else {
      this._removeItemsInternal(toRemove);
    }
  }

  private _removeItemsInternal(items: SelectableItem[]) {
    this.internalSelectedItems = this.internalSelectedItems.filter(
      (i) => !items.find((r) => r.id === i.id)
    );

    items.forEach((item) => {
      delete this.pendingSelection[item.id];
    });

    this.resetSelectableItems();
    this.emitSelectionChange();
  }

  emitSelectionChange() {
    this.onChange(this.internalSelectedItems.map((x) => x.id));
    this.selectionChange.emit(this.internalSelectedItems);
  }
}
